import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import moment from "moment";
import { companyDayKey, formatCompanyDate, companyToday } from "../../helpers/companyTime";
import { useSelector } from "react-redux";
import { GET_USER_KPIS } from "../../api/kpi";
import { formatKpiPercent } from "../../helpers/scoreAggregate";

/**
 * Latest activity — the individual records that add up to the number.
 *
 * The other half of what sits behind any metric (Standing being the first), so
 * like Standing this is not specific to the self-generated card it was drawn
 * for: every metric has rows behind its total.
 *
 * Source is `GET /ekpi/users/:companyId?userId=&kpiId=`, already deployed and
 * already used by the Scorekeeper page. Verified against production: it returns
 * one document per entry carrying `amount`, `createdAt`, `ownerId` and a
 * `metadata` bag (which holds `tag`, and `jobAmount` when the credited amount
 * differs from what the job was worth).
 *
 * What it does NOT carry is the customer or the address — those live on the
 * linked Job document (`refId`/`refModel`), a second lookup this does not make.
 * So a row reads "Aug 11 · solo tech · $520", not a street name. Better a true
 * short row than a fabricated rich one.
 */

const fmt = (kind, n, meta) => {
  const v = Number(n) || 0;
  if (kind === "$") return `$${Math.round(v).toLocaleString()}`;
  if (kind === "%") {
    const pct = Math.abs(v) <= 1 ? v : v / 100;
    return formatKpiPercent(meta, pct);
  }
  return Math.round(v).toLocaleString();
};

// Yesterday is the only bounded window — everything else runs to company today.
const rangeOf = (period, customRange = null) => {
  const today = companyToday();
  if (period === "today") return { from: today.clone().startOf("day"), to: today.clone().endOf("day") };
  if (period === "yesterday") {
    const d = today.clone().subtract(1, "day");
    return { from: d.clone().startOf("day"), to: d.clone().endOf("day") };
  }
  if (period === "week") return { from: today.clone().startOf("week"), to: today.clone().endOf("day") };
  if (period === "lastWeek") {
    const start = today.clone().subtract(1, "week").startOf("week");
    return { from: start, to: start.clone().endOf("week") };
  }
  if (period === "lastMonth") {
    const start = today.clone().subtract(1, "month").startOf("month");
    return { from: start, to: start.clone().endOf("month") };
  }
  if (period === "quarter") return { from: today.clone().startOf("quarter"), to: today.clone().endOf("day") };
  if (period === "ytd" || period === "year") return { from: today.clone().startOf("year"), to: today.clone().endOf("day") };
  if (period === "custom" && customRange) {
    const from = customRange.from
      ? moment(customRange.from).startOf("day")
      : today.clone().startOf("month");
    let to = customRange.to
      ? moment(customRange.to).endOf("day")
      : today.clone().endOf("day");
    if (to.isAfter(today)) to = today.clone().endOf("day");
    return { from, to };
  }
  return { from: today.clone().startOf("month"), to: today.clone().endOf("day") };
};

const Activity = ({ metricId, metricTitle = "", ownerId = null, kind = "number", period = "month", customRange = null, limit = 40 }) => {
  const metricMeta = { title: metricTitle };
  const companyId = useSelector((state) => state.authReducer.userDetails?.companyId);
  const [rows, setRows] = useState(null); // null = still loading, [] = genuinely empty
  const [failed, setFailed] = useState(false);
  const alive = useRef(true);
  const metricIds = useMemo(() => {
    if (Array.isArray(metricId)) return metricId.map(String).filter(Boolean);
    if (metricId == null || metricId === "") return [];
    return [String(metricId)];
  }, [metricId]);
  const metricKey = metricIds.join("|");

  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  useEffect(() => {
    if (!companyId || !metricIds.length) return undefined;
    setRows(null);
    setFailed(false);
    Promise.all(
      metricIds.map((id) =>
        GET_USER_KPIS(companyId, ownerId || undefined, id)
          .then(({ data }) => (Array.isArray(data) ? data : []))
          .catch(() => []),
      ),
    )
      .then((parts) => {
        if (!alive.current) return;
        const { from, to } = rangeOf(period, customRange);
        const seen = new Set();
        const list = parts
          .flat()
          .filter((e) => e && (!ownerId || e.ownerId === ownerId))
          .filter((e) => {
            if (!e.createdAt) return false;
            const d = companyDayKey(e.createdAt);
            return d >= from.format("YYYY-MM-DD") && d <= to.format("YYYY-MM-DD");
          })
          .filter((e) => {
            const key = e._id ? String(e._id) : `${e.ownerId}|${e.createdAt}|${e.amount}|${e.kpiId || ""}`;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          })
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
          .slice(0, limit);
        setRows(list);
      })
      .catch(() => { if (alive.current) { setFailed(true); setRows([]); } });
    return undefined;
    // metricKey stands in for metricIds (stable string join)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, metricKey, ownerId, period, customRange, limit]);

  const total = useMemo(
    () => (rows || []).reduce((sum, e) => sum + (Number(e.amount) || 0), 0),
    [rows],
  );

  /* Loading is not the same as nothing. A list that renders empty while the
     request is still out reads as "you did no work", which on a card about
     someone's pay is a wrong statement rather than a blank one. */
  if (rows === null) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
        {[0, 1, 2].map((i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 11, padding: "8px 0" }}>
            <span style={{ width: 42, height: 9, borderRadius: 5, background: "#F1F4F8" }} />
            <span style={{ flex: 1, height: 9, borderRadius: 5, background: "#F4F6F9" }} />
            <span style={{ width: 46, height: 9, borderRadius: 5, background: "#F1F4F8" }} />
          </div>
        ))}
      </div>
    );
  }

  if (!rows.length) {
    return (
      <p style={{ margin: 0, fontSize: 11.5, color: "var(--tvd-muted, #98A2B3)", lineHeight: 1.6 }}>
        {failed
          ? "Couldn’t load the entries just now."
          : "Nothing recorded for this period yet. Entries appear here as they come in."}
      </p>
    );
  }

  return (
    <>
      {/* Four rows, then scroll. A block must never be what decides the card's
          height — the row it sits in does that, and an unbounded list ran the
          card well past the card beside it. */}
      <div style={{ maxHeight: 148, overflowY: "auto", paddingRight: 4, marginRight: -4 }}>
        {rows.map((e, i) => {
          const tag = e.metadata && typeof e.metadata.tag === "string" ? e.metadata.tag : "";
          return (
            <div
              key={e._id || `${e.entryId}${i}`}
              style={{
                display: "flex", alignItems: "baseline", gap: 11, padding: "8px 0",
                fontSize: 11.5, borderBottom: i === rows.length - 1 ? 0 : "1px solid var(--tvd-hair, #F4F6F9)",
              }}
            >
              <span style={{ width: 42, flex: "none", color: "var(--tvd-muted, #98A2B3)", fontSize: 10.5 }}>
                {formatCompanyDate(e.createdAt)}
              </span>
              <span style={{
                flex: 1, minWidth: 0, color: "var(--tvd-body, #475467)",
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              }}>
                {tag || "Entry"}
              </span>
              <span style={{ fontWeight: 600, color: "var(--tvd-ink, #0F172A)", fontVariantNumeric: "tabular-nums" }}>
                {fmt(kind, e.amount, metricMeta)}
              </span>
            </div>
          );
        })}
      </div>
      <div style={{
        fontSize: 10.5, color: "var(--tvd-muted, #98A2B3)", marginTop: 10, paddingTop: 9,
        borderTop: "1px solid var(--tvd-hair, #F4F6F9)", display: "flex", justifyContent: "space-between",
      }}>
        <span>{rows.length} {rows.length === 1 ? "entry" : "entries"}</span>
        <span style={{ fontVariantNumeric: "tabular-nums" }}>{fmt(kind, total, metricMeta)}</span>
      </div>
    </>
  );
};

Activity.propTypes = {
  metricId: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  metricTitle: PropTypes.string,
  ownerId: PropTypes.string,
  kind: PropTypes.string,
  period: PropTypes.string,
  // eslint-disable-next-line react/forbid-prop-types
  customRange: PropTypes.object,
  limit: PropTypes.number,
};

Activity.defaultProps = {
  metricId: null,
  metricTitle: "",
  ownerId: null,
  kind: "number",
  period: "month",
  customRange: null,
  limit: 40,
};

export default Activity;
